/**
 * Procedural ambience library — the product's nature-sound beds.
 *
 * Every bed is synthesized deterministically (seeded xoshiro per asset id)
 * at load time: tiny footprint, no licensing, no obvious loop seams (edges
 * are raised-cosine faded). This is the PRODUCT library; the conformance
 * test assets (conformance/test-assets.ts) are a frozen cross-engine
 * contract and must not change.
 *
 * Kotlin port for Android is required before bundled presets may use
 * ambience layers there (PresetAssetsTest enforces this).
 */

import type { AssetResolver, StereoBuffer } from "./renderer.ts";
import { fnv1a32, Xoshiro128pp } from "./rng.ts";

export interface AmbienceEntry {
  id: string;
  label: string;
  seconds: number;
}

export const AMBIENCE_MANIFEST: AmbienceEntry[] = [
  { id: "rain_soft", label: "Soft rain", seconds: 15 },
  { id: "rain_heavy", label: "Heavy rain", seconds: 15 },
  { id: "ocean_waves", label: "Ocean waves", seconds: 24 },
  { id: "wind", label: "Wind", seconds: 18 },
  { id: "stream", label: "Stream", seconds: 14 },
  { id: "night_crickets", label: "Night crickets", seconds: 16 },
  { id: "fire_crackle", label: "Fireplace", seconds: 15 },
];

/** One-pole lowpass coefficient for cutoff fc. */
function lpCoeff(fc: number, sr: number): number {
  return 1 - Math.exp((-2 * Math.PI * fc) / sr);
}

function edgeFade(buf: StereoBuffer, sr: number, sec = 0.06): void {
  const fade = Math.round(sec * sr);
  const n = buf.left.length;
  for (let i = 0; i < fade && i < n; i++) {
    const g = 0.5 * (1 - Math.cos((Math.PI * i) / fade));
    buf.left[i]! *= g;
    buf.right[i]! *= g;
    buf.left[n - 1 - i]! *= g;
    buf.right[n - 1 - i]! *= g;
  }
}

function alloc(seconds: number, sr: number): StereoBuffer {
  const frames = Math.round(seconds * sr);
  return { left: new Float32Array(frames), right: new Float32Array(frames) };
}

// ------------------------------------------------------------------- rain

/**
 * Rain = a hiss bed (droplet mist) + thousands of individual droplet
 * impacts: short high-frequency noise bursts with exponential decay,
 * randomly panned, two size classes.
 */
function makeRain(sr: number, seconds: number, heavy: boolean): StereoBuffer {
  const buf = alloc(seconds, sr);
  const n = buf.left.length;
  const rng = new Xoshiro128pp(fnv1a32(heavy ? "amb:rain_heavy" : "amb:rain_soft"));

  // Mist bed: lowpassed white noise, quiet.
  const bedGain = heavy ? 0.045 : 0.022;
  const aBed = lpCoeff(heavy ? 2600 : 1800, sr);
  let bedL = 0;
  let bedR = 0;
  for (let i = 0; i < n; i++) {
    bedL += aBed * (rng.nextFloat() - bedL);
    bedR += aBed * (rng.nextFloat() - bedR);
    buf.left[i] = bedL * bedGain * 8;
    buf.right[i] = bedR * bedGain * 8;
  }

  // Droplets.
  const dropsPerSec = heavy ? 900 : 380;
  const dropCount = Math.round(dropsPerSec * seconds);
  for (let d = 0; d < dropCount; d++) {
    const start = Math.floor(((rng.nextFloat() + 1) / 2) * n);
    const size = (rng.nextFloat() + 1) / 2; // 0..1
    const big = size > (heavy ? 0.93 : 0.96);
    const lenMs = big ? 6 + size * 8 : 1.5 + size * 3;
    const len = Math.min(Math.round((lenMs / 1000) * sr), n - start);
    const amp = (big ? 0.34 : 0.16) * size * size;
    const tau = len / (big ? 3.2 : 4.5);
    const pan = rng.nextFloat(); // -1..1
    const gl = Math.min(1, 1 - pan) * amp;
    const gr = Math.min(1, 1 + pan) * amp;
    // Highpass-ish burst: difference of consecutive noise samples.
    let prev = 0;
    for (let k = 0; k < len; k++) {
      const w = rng.nextFloat();
      const hp = w - prev * 0.6;
      prev = w;
      const env = Math.exp(-k / tau);
      const v = hp * env;
      buf.left[start + k]! += v * gl;
      buf.right[start + k]! += v * gr;
    }
  }

  edgeFade(buf, sr);
  return buf;
}

// ------------------------------------------------------------------ ocean

/** Ocean = brown surf bed swelling at ~0.08 Hz with a hiss "wash" that follows. */
function makeOcean(sr: number, seconds: number): StereoBuffer {
  const buf = alloc(seconds, sr);
  const n = buf.left.length;
  const rng = new Xoshiro128pp(fnv1a32("amb:ocean_waves"));
  const aHiss = lpCoeff(2600, sr);
  let brownL = 0;
  let brownR = 0;
  let hissL = 0;
  let hissR = 0;
  // Two staggered swell frequencies so waves feel irregular.
  const f1 = 0.078;
  const f2 = 0.113;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const swellRaw = 0.6 * Math.sin(2 * Math.PI * f1 * t) + 0.4 * Math.sin(2 * Math.PI * f2 * t + 1.7);
    const swell = Math.pow(0.5 + 0.5 * swellRaw, 1.6); // shaped: long troughs, rolling crests
    const wash = Math.pow(0.5 + 0.5 * Math.sin(2 * Math.PI * f1 * t + 0.9), 3); // hiss follows the break
    brownL = (brownL + 0.02 * rng.nextFloat()) / 1.02;
    brownR = (brownR + 0.02 * rng.nextFloat()) / 1.02;
    hissL += aHiss * (rng.nextFloat() - hissL);
    hissR += aHiss * (rng.nextFloat() - hissR);
    buf.left[i] = brownL * 3.2 * (0.25 + 0.75 * swell) + hissL * 0.16 * wash;
    buf.right[i] = brownR * 3.2 * (0.25 + 0.75 * swell) + hissR * 0.16 * wash;
  }
  edgeFade(buf, sr);
  return buf;
}

// ------------------------------------------------------------------- wind

/** Wind = noise through a slowly wandering lowpass, with gusts. */
function makeWind(sr: number, seconds: number): StereoBuffer {
  const buf = alloc(seconds, sr);
  const n = buf.left.length;
  const rng = new Xoshiro128pp(fnv1a32("amb:wind"));
  let fc = 500;
  let gust = 0.6;
  let l1 = 0;
  let l2 = 0;
  let r1 = 0;
  let r2 = 0;
  for (let i = 0; i < n; i++) {
    // Random-walk the cutoff and gust level (updated every sample, tiny steps).
    fc = Math.min(1100, Math.max(240, fc + rng.nextFloat() * 1.1));
    gust = Math.min(1, Math.max(0.28, gust + rng.nextFloat() * 0.0012));
    const a = lpCoeff(fc, sr);
    // Two cascaded one-poles ≈ steeper, smoother howl.
    l1 += a * (rng.nextFloat() - l1);
    l2 += a * (l1 - l2);
    r1 += a * (rng.nextFloat() - r1);
    r2 += a * (r1 - r2);
    buf.left[i] = l2 * 2.6 * gust;
    buf.right[i] = r2 * 2.6 * gust;
  }
  edgeFade(buf, sr);
  return buf;
}

// ------------------------------------------------------------------ stream

/** Stream = several burbling bands with wandering centers + bright hiss. */
function makeStream(sr: number, seconds: number): StereoBuffer {
  const buf = alloc(seconds, sr);
  const n = buf.left.length;
  const rng = new Xoshiro128pp(fnv1a32("amb:stream"));
  const bands = [
    { fc: 420, amp: 0.5, pan: -0.4, lp: 0, lp2: 0 },
    { fc: 700, amp: 0.42, pan: 0.35, lp: 0, lp2: 0 },
    { fc: 1050, amp: 0.3, pan: -0.1, lp: 0, lp2: 0 },
    { fc: 1500, amp: 0.22, pan: 0.15, lp: 0, lp2: 0 },
  ];
  const aHiss = lpCoeff(5200, sr);
  let hiss = 0;
  for (let i = 0; i < n; i++) {
    let l = 0;
    let r = 0;
    for (const b of bands) {
      b.fc = Math.min(b.fc * 1.9, Math.max(b.fc * 0.55, b.fc + rng.nextFloat() * 2.2));
      const a = lpCoeff(b.fc, sr);
      const w = rng.nextFloat();
      b.lp += a * (w - b.lp);
      b.lp2 += lpCoeff(b.fc * 0.45, sr) * (b.lp - b.lp2);
      const band = (b.lp - b.lp2) * 1.55 * b.amp; // crude bandpass = LP - slower LP
      l += band * Math.min(1, 1 - b.pan);
      r += band * Math.min(1, 1 + b.pan);
    }
    hiss += aHiss * (rng.nextFloat() - hiss);
    buf.left[i] = l + hiss * 0.04;
    buf.right[i] = r + hiss * 0.04;
  }
  edgeFade(buf, sr);
  return buf;
}

// --------------------------------------------------------------- crickets

/** Night = near-silence + a few cricket voices chirping in trains. */
function makeCrickets(sr: number, seconds: number): StereoBuffer {
  const buf = alloc(seconds, sr);
  const n = buf.left.length;
  const rng = new Xoshiro128pp(fnv1a32("amb:night_crickets"));

  // Faint night-air bed.
  const aBed = lpCoeff(500, sr);
  let bed = 0;
  for (let i = 0; i < n; i++) {
    bed += aBed * (rng.nextFloat() - bed);
    buf.left[i] = bed * 0.07;
    buf.right[i] = bed * 0.07;
  }

  const voices = 5;
  for (let v = 0; v < voices; v++) {
    const f0 = 4100 + ((rng.nextFloat() + 1) / 2) * 1100; // carrier 4.1–5.2 kHz
    const pulseHz = 38 + ((rng.nextFloat() + 1) / 2) * 18; // wing-strokes per second
    const pan = rng.nextFloat() * 0.9;
    const gl = Math.min(1, 1 - pan) * 0.13;
    const gr = Math.min(1, 1 + pan) * 0.13;
    let t = ((rng.nextFloat() + 1) / 2) * 1.2; // start offset
    while (t < seconds) {
      const pulses = 3 + Math.floor(((rng.nextFloat() + 1) / 2) * 4);
      for (let p = 0; p < pulses; p++) {
        const pulseStart = Math.round((t + p / pulseHz) * sr);
        const len = Math.round(0.016 * sr);
        for (let k = 0; k < len && pulseStart + k < n; k++) {
          const env = 0.5 * (1 - Math.cos((2 * Math.PI * k) / len)); // hann
          const s = Math.sin((2 * Math.PI * f0 * k) / sr) * env;
          buf.left[pulseStart + k]! += s * gl;
          buf.right[pulseStart + k]! += s * gr;
        }
      }
      t += pulses / pulseHz + 0.5 + ((rng.nextFloat() + 1) / 2) * 2.2; // gap
    }
  }
  edgeFade(buf, sr);
  return buf;
}

// ------------------------------------------------------------------- fire

/** Fire = warm rumble bed + sparse sharp crackles + occasional pops. */
function makeFire(sr: number, seconds: number): StereoBuffer {
  const buf = alloc(seconds, sr);
  const n = buf.left.length;
  const rng = new Xoshiro128pp(fnv1a32("amb:fire_crackle"));

  const aBed = lpCoeff(140, sr);
  let bedL = 0;
  let bedR = 0;
  for (let i = 0; i < n; i++) {
    bedL += aBed * (rng.nextFloat() - bedL);
    bedR += aBed * (rng.nextFloat() - bedR);
    buf.left[i] = bedL * 1.4;
    buf.right[i] = bedR * 1.4;
  }

  const events = Math.round(28 * seconds);
  for (let e = 0; e < events; e++) {
    const start = Math.floor(((rng.nextFloat() + 1) / 2) * n);
    const size = (rng.nextFloat() + 1) / 2;
    const pop = size > 0.94;
    const len = Math.min(Math.round(((pop ? 9 : 1.2 + size * 2.5) / 1000) * sr), n - start);
    const amp = (pop ? 0.5 : 0.3) * size * size;
    const tau = len / (pop ? 2.6 : 5);
    const pan = rng.nextFloat() * 0.7;
    const gl = Math.min(1, 1 - pan) * amp;
    const gr = Math.min(1, 1 + pan) * amp;
    let prev = 0;
    for (let k = 0; k < len; k++) {
      const w = rng.nextFloat();
      const hp = w - prev * (pop ? 0.2 : 0.7);
      prev = w;
      const v = hp * Math.exp(-k / tau);
      buf.left[start + k]! += v * gl;
      buf.right[start + k]! += v * gr;
    }
  }
  edgeFade(buf, sr);
  return buf;
}

// ---------------------------------------------------------------- factory

export function createAmbience(id: string, sampleRate: number): StereoBuffer | null {
  const entry = AMBIENCE_MANIFEST.find((e) => e.id === id);
  if (!entry) return null;
  switch (id) {
    case "rain_soft": return makeRain(sampleRate, entry.seconds, false);
    case "rain_heavy": return makeRain(sampleRate, entry.seconds, true);
    case "ocean_waves": return makeOcean(sampleRate, entry.seconds);
    case "wind": return makeWind(sampleRate, entry.seconds);
    case "stream": return makeStream(sampleRate, entry.seconds);
    case "night_crickets": return makeCrickets(sampleRate, entry.seconds);
    case "fire_crackle": return makeFire(sampleRate, entry.seconds);
    default: return null;
  }
}

/** Resolver over the manifest, with per-id caching. */
export function makeAmbienceResolver(sampleRate: number): AssetResolver {
  const cache = new Map<string, StereoBuffer>();
  return (ref, kind) => {
    if (kind !== "ambience") return null;
    const hit = cache.get(ref);
    if (hit) return hit;
    const made = createAmbience(ref, sampleRate);
    if (made) cache.set(ref, made);
    return made;
  };
}
